// frontend/src/components/Diario/ModalAtividadeFisica.jsx
// Modal completo para registro de atividade física

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Activity, Clock, TrendingUp, Dumbbell, Save, X, ChevronDown } from 'lucide-react';
import Modal from '../UI/Modal';
import DropdownMenu from '../UI/DropdownMenu.jsx';

const TURNOS = ['Manhã', 'Tarde', 'Noite', 'Madrugada'];

const TIPOS_ATIVIDADE = [
    'Caminhada',
    'Exercícios de Mobilidade',
    'Fisioterapia',
    'Alongamento',
    'Exercícios de Força',
    'Exercícios Respiratórios',
    'Hidroterapia',
    'Outro'
];

const INTENSIDADES = [
    { value: 'leve', label: 'Leve', color: 'bg-green-100 text-green-800' },
    { value: 'moderada', label: 'Moderada', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'intensa', label: 'Intensa', color: 'bg-red-100 text-red-800' }
];

/**
 * Modal completo para registro de atividade física
 * 
 * @param {boolean} isOpen - Controla a visibilidade do modal
 * @param {function} onClose - Função para fechar o modal
 * @param {string} pacienteId - ID do paciente
 * @param {function} onCreateRegistro - Função para criar registro
 * @param {function} onRegistroSucesso - Callback após sucesso
 */
const ModalAtividadeFisica = ({ isOpen, onClose, pacienteId, onCreateRegistro, onRegistroSucesso }) => {
    const [formData, setFormData] = useState({
        tipo_atividade: '',
        duracao_minutos: '',
        intensidade: '',
        equipamentos_utilizados: '',
        observacoes: '',
        turno: 'Manhã'
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Limpar erro do campo
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.tipo_atividade.trim()) {
            newErrors.tipo_atividade = 'Tipo de atividade é obrigatório';
        }

        if (!formData.duracao_minutos || formData.duracao_minutos <= 0) {
            newErrors.duracao_minutos = 'Duração deve ser maior que zero';
        }

        if (!formData.intensidade) {
            newErrors.intensidade = 'Intensidade é obrigatória';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const dataToSend = {
                paciente_id: pacienteId,
                tipo_registro: 'Atividade Fisica', // Valor que será aceito pelo backend
                observacoes: formData.observacoes || null,
                turno: formData.turno,
                detalhes: {
                    tipo_atividade: formData.tipo_atividade,
                    duracao_minutos: parseInt(formData.duracao_minutos),
                    intensidade: formData.intensidade,
                    equipamentos_utilizados: formData.equipamentos_utilizados || null
                }
            };

            await onCreateRegistro(dataToSend);
            toast.success('Atividade física registrada com sucesso!');
            
            if (onRegistroSucesso) {
                onRegistroSucesso();
            }
            
            // Resetar formulário
            setFormData({
                tipo_atividade: '',
                duracao_minutos: '',
                intensidade: '',
                equipamentos_utilizados: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        } catch (error) {
            toast.error(error.message || 'Erro ao registrar atividade física');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                tipo_atividade: '',
                duracao_minutos: '',
                intensidade: '',
                equipamentos_utilizados: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Registrar Atividade Física" size="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tipo de Atividade */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Activity size={16} className="inline mr-2" />
                        Tipo de Atividade <span className="text-red-500">*</span>
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                className={`
                                    mt-1 w-full flex items-center justify-between
                                    border rounded-md shadow-sm p-3 text-sm
                                    focus:ring-emerald-500 focus:border-emerald-500
                                    ${errors.tipo_atividade ? 'border-red-300' : 'border-gray-300'}
                                    ${formData.tipo_atividade ? 'text-gray-900' : 'text-gray-500'}
                                `}
                            >
                                <span>{formData.tipo_atividade || 'Selecione o tipo de atividade...'}</span>
                                <ChevronDown size={18} className="text-gray-400" />
                            </button>
                        }
                        items={TIPOS_ATIVIDADE.map(tipo => ({
                            id: tipo,
                            label: tipo,
                            onClick: () => {
                                setFormData(prev => ({ ...prev, tipo_atividade: tipo }));
                                if (errors.tipo_atividade) {
                                    setErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors.tipo_atividade;
                                        return newErrors;
                                    });
                                }
                            }
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                    {errors.tipo_atividade && (
                        <p className="mt-1 text-xs text-red-600">{errors.tipo_atividade}</p>
                    )}
                </div>

                {/* Duração e Intensidade - Grid */}
                <div className="grid grid-cols-2 gap-4">
                    {/* Duração */}
                    <div>
                        <label htmlFor="duracao_minutos" className="block text-sm font-medium text-gray-700 mb-2">
                            <Clock size={16} className="inline mr-2" />
                            Duração (minutos) <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            name="duracao_minutos"
                            id="duracao_minutos"
                            value={formData.duracao_minutos}
                            onChange={handleChange}
                            min="1"
                            max="480"
                            required
                            placeholder="Ex: 30"
                            className={`mt-1 block w-full border rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500 ${
                                errors.duracao_minutos ? 'border-red-300' : 'border-gray-300'
                            }`}
                        />
                        {errors.duracao_minutos && (
                            <p className="mt-1 text-xs text-red-600">{errors.duracao_minutos}</p>
                        )}
                    </div>

                    {/* Intensidade */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <TrendingUp size={16} className="inline mr-2" />
                            Intensidade <span className="text-red-500">*</span>
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    className={`
                                        mt-1 w-full flex items-center justify-between
                                        border rounded-md shadow-sm p-3 text-sm
                                        focus:ring-emerald-500 focus:border-emerald-500
                                        ${errors.intensidade ? 'border-red-300' : 'border-gray-300'}
                                        ${formData.intensidade ? 'text-gray-900' : 'text-gray-500'}
                                    `}
                                >
                                    <span>
                                        {formData.intensidade 
                                            ? INTENSIDADES.find(i => i.value === formData.intensidade)?.label
                                            : 'Selecione a intensidade...'
                                        }
                                    </span>
                                    <ChevronDown size={18} className="text-gray-400" />
                                </button>
                            }
                            items={INTENSIDADES.map(int => ({
                                id: int.value,
                                label: int.label,
                                className: int.color,
                                onClick: () => {
                                    setFormData(prev => ({ ...prev, intensidade: int.value }));
                                    if (errors.intensidade) {
                                        setErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.intensidade;
                                            return newErrors;
                                        });
                                    }
                                }
                            }))}
                            position="bottom-left"
                            className="w-full"
                        />
                        {errors.intensidade && (
                            <p className="mt-1 text-xs text-red-600">{errors.intensidade}</p>
                        )}
                    </div>
                </div>

                {/* Equipamentos Utilizados */}
                <div>
                    <label htmlFor="equipamentos_utilizados" className="block text-sm font-medium text-gray-700 mb-2">
                        <Dumbbell size={16} className="inline mr-2" />
                        Equipamentos Utilizados (Opcional)
                    </label>
                    <input
                        type="text"
                        name="equipamentos_utilizados"
                        id="equipamentos_utilizados"
                        value={formData.equipamentos_utilizados}
                        onChange={handleChange}
                        placeholder="Ex: Bola de exercício, Halteres, Esteira..."
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                {/* Turno */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Turno <span className="text-red-500">*</span>
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                className="mt-1 w-full flex items-center justify-between border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                            >
                                <span>{formData.turno}</span>
                                <ChevronDown size={18} className="text-gray-400" />
                            </button>
                        }
                        items={TURNOS.map(turno => ({
                            id: turno,
                            label: turno,
                            onClick: () => {
                                setFormData(prev => ({ ...prev, turno }));
                            }
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* Observações */}
                <div>
                    <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-2">
                        Observações (Opcional)
                    </label>
                    <textarea
                        name="observacoes"
                        id="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        placeholder="Adicione observações sobre a atividade física realizada..."
                        rows="4"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                    >
                        <X size={18} />
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md transition disabled:opacity-50"
                    >
                        {loading ? (
                            <>Salvando...</>
                        ) : (
                            <>
                                <Save size={18} />
                                Registrar Atividade
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ModalAtividadeFisica;

